import { useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { useToast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";
import { InlineSubscribeForm } from "@/components/InlineSubscribeForm";
import { 
  Users, 
  Zap, 
  ArrowRight, 
  Loader2, 
  CheckCircle2,
  Phone,
  FileBarChart,
  Calendar
} from "lucide-react";

interface WelcomePopupModalProps {
  isOpen: boolean;
  onClose: () => void;
}

export const WelcomePopupModal = ({ isOpen, onClose }: WelcomePopupModalProps) => {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    company: ""
  });
  const { toast } = useToast();

  const handleSkip = () => {
    localStorage.setItem("snake-signals-popup-shown", "true");
    onClose();
  };

  const handleReferralSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!formData.name.trim() || !formData.email.trim() || !formData.company.trim()) {
      toast({
        title: "Please fill in all fields",
        variant: "destructive"
      });
      return;
    }

    setIsSubmitting(true);
    
    const referralData = {
      name: formData.name.trim(),
      email: formData.email.trim(),
      company: formData.company.trim()
    };
    
    const { error } = await supabase
      .from('referrals')
      .insert(referralData);
    
    if (error) {
      console.error('Error submitting referral:', error);
      setIsSubmitting(false);
      toast({
        title: "Something went wrong",
        description: "Please try again later.",
        variant: "destructive"
      });
      return;
    }
    
    // Send Slack notification
    supabase.functions.invoke('notify-slack-referral', {
      body: referralData
    }).catch(err => console.error('Slack notification failed:', err));
    
    setIsSubmitting(false);
    setIsSubmitted(true);
    localStorage.setItem("snake-signals-popup-shown", "true");
    
    toast({
      title: "Thanks for reaching out!",
      description: "I'll be in touch within 24 hours to discuss how I can help."
    });
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-lg cyber-border bg-card/95 backdrop-blur-xl p-0 overflow-hidden">
        <DialogHeader className="text-center p-6 pb-4">
          <DialogTitle className="text-2xl font-bold font-orbitron bg-gradient-to-r from-primary via-accent to-secondary bg-clip-text text-transparent">
            Welcome to Snake Signals
          </DialogTitle>
          <DialogDescription className="text-muted-foreground">
            Python market intelligence for developers and hiring managers
          </DialogDescription>
        </DialogHeader>

        <Tabs defaultValue="subscribe" className="w-full">
          <TabsList className="grid w-full grid-cols-2 mx-6 mb-4" style={{ width: 'calc(100% - 3rem)' }}>
            <TabsTrigger value="subscribe" className="gap-2">
              <Zap size={16} />
              Subscribe
            </TabsTrigger>
            <TabsTrigger value="hire" className="gap-2">
              <Users size={16} />
              Hiring Help
            </TabsTrigger>
          </TabsList>

          <TabsContent value="subscribe" className="px-6 pb-6 space-y-4 mt-0">
            <div className="text-center space-y-3">
              <p className="text-sm text-muted-foreground">
                Get weekly Python salary data, hiring trends, and market intelligence delivered every Tuesday.
              </p>
              <InlineSubscribeForm 
                variant="default" 
                buttonText="Send me the signals"
              />
            </div>
          </TabsContent>

          <TabsContent value="hire" className="px-6 pb-6 mt-0">
            {isSubmitted ? (
              <div className="text-center py-4">
                <CheckCircle2 className="w-12 h-12 text-primary mx-auto mb-3" />
                <h3 className="text-lg font-bold mb-2">Thanks for reaching out!</h3>
                <p className="text-sm text-muted-foreground">
                  I'll be in touch within 24 hours to discuss how I can help your team.
                </p>
              </div>
            ) : (
              <div className="space-y-4">
                <div className="flex gap-4 text-xs text-muted-foreground mb-4">
                  <div className="flex items-center gap-1.5">
                    <Phone size={14} className="text-primary" />
                    <span>Free 30-min call</span>
                  </div>
                  <div className="flex items-center gap-1.5">
                    <FileBarChart size={14} className="text-primary" />
                    <span>Custom salary report</span>
                  </div>
                </div>
                
                <form onSubmit={handleReferralSubmit} className="space-y-3">
                  <div className="space-y-1.5">
                    <Label htmlFor="popup-name" className="text-sm">Your Name</Label>
                    <Input
                      id="popup-name"
                      placeholder="John Smith"
                      value={formData.name}
                      onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                      disabled={isSubmitting}
                      className="bg-background/50"
                    />
                  </div>
                  
                  <div className="space-y-1.5">
                    <Label htmlFor="popup-email" className="text-sm">Work Email</Label>
                    <Input
                      id="popup-email"
                      type="email"
                      placeholder="john@company.com"
                      value={formData.email}
                      onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                      disabled={isSubmitting}
                      className="bg-background/50"
                    />
                  </div>
                  
                  <div className="space-y-1.5">
                    <Label htmlFor="popup-company" className="text-sm">Company Name</Label>
                    <Input
                      id="popup-company"
                      placeholder="Acme Inc"
                      value={formData.company}
                      onChange={(e) => setFormData({ ...formData, company: e.target.value })}
                      disabled={isSubmitting}
                      className="bg-background/50"
                    />
                  </div>
                  
                  <Button 
                    type="submit" 
                    className="w-full gap-2"
                    disabled={isSubmitting}
                  >
                    {isSubmitting ? (
                      <>
                        <Loader2 className="w-4 h-4 animate-spin" />
                        Submitting...
                      </>
                    ) : (
                      <>
                        Get My Free Consultation
                        <ArrowRight className="w-4 h-4" />
                      </>
                    )}
                  </Button>
                </form>
                
                <div className="relative">
                  <div className="absolute inset-0 flex items-center">
                    <div className="w-full border-t border-border"></div>
                  </div>
                  <div className="relative flex justify-center text-xs uppercase">
                    <span className="bg-card px-2 text-muted-foreground">or</span>
                  </div>
                </div>
                
                <Button 
                  variant="outline" 
                  className="w-full gap-2"
                  asChild
                >
                  <a 
                    href="https://calendly.com/python-recruitment" 
                    target="_blank" 
                    rel="noopener noreferrer"
                  >
                    <Calendar className="w-4 h-4" />
                    Book a Call Directly
                  </a>
                </Button>
              </div>
            )}
          </TabsContent>
        </Tabs>

        <div className="px-6 pb-4 pt-2 border-t border-border">
          <Button 
            variant="ghost" 
            onClick={handleSkip}
            className="w-full text-muted-foreground hover:text-foreground text-sm"
          >
            Skip for now
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};
